/**
 * Funcionalidades como loja (#560): a vitrine do que a organização pode
 * adicionar, e o detalhe de cada item.
 *
 * ── Por que uma loja, e não a lista de chaves ─────────────────────────────
 *
 * A seção era uma lista de chaves de recurso com "Ligar/Desligar", e as
 * integrações moravam em outro lugar — canais em Conexões, portais só depois
 * de alguém da Cliqfy ligar um. Quem administra a organização não tinha onde
 * ver o que existe para adicionar. A loja junta os três numa vitrine, por
 * categoria e com busca, e a pessoa adiciona sozinha.
 *
 * ── O desenho (opção A, 09/10/2026) ───────────────────────────────────────
 *
 * Uma capa com o mosaico dos apps ao fundo e a busca na frente; abaixo, uma
 * grade só, em que cada cartão abre com uma faixa na cor da marca. É o formato
 * das lojas de apps que a pessoa já conhece, e a faixa é o que faz o canal ser
 * reconhecido antes de o nome ser lido.
 *
 * ── Adicionar não ganha um segundo caminho ────────────────────────────────
 *
 * Canal precisa de configuração, e o assistente de Conexões já faz isso: a
 * loja leva até ele, no passo do canal escolhido. Funcionalidade e portal são
 * uma chave por organização, e ligar continua sendo homologar (princípio 08),
 * com o registro de antes: quem ligou, e quando.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { TipoDeCanal } from '@cliqcrm/contracts';
import {
  Botao,
  Campo,
  CampoSelecao,
  Chip,
  Dialogo,
  Icone,
  Identificador,
  Selo,
  type NomeDoIcone,
} from '@/shared/ui';
import { Estado } from '@/shared/ui/estados/estados';
import { Faixa } from '@/shared/ui/estados/faixa';
import {
  useApi,
  useCanaisConectados,
  useCatalogoDePlataformas,
  useFlags,
} from '@/shared/api/consultas';
import { useSessao } from '@/shared/sessao/contexto';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { aplicativoDaMeta } from '@/shared/config/configuracao';
import { iconeDoCanal } from './canais';
import type { UsoDeStepUp } from '@/features/acesso/confirmar-identidade';
import {
  CATEGORIAS_DA_LOJA,
  filtrarLoja,
  montarLoja,
  ROTULO_DO_ESTADO,
  type CategoriaDaLoja,
  type FontesDaLoja,
  type ItemDaLoja,
} from '../lib/catalogo-da-loja';
import './loja.css';

export interface PropsDaLoja {
  readonly stepUp: UsoDeStepUp;
  /** Quem vê integração vê canais e portais na loja; os outros, não. */
  readonly veIntegracao: boolean;
  /** `configuracao × editar`: liga e desliga funcionalidade e portal. */
  readonly editaFuncionalidades: boolean;
  /** `integracao × editar`: adiciona canal pelo assistente de Conexões. */
  readonly conectaCanais: boolean;
  /** Leva ao assistente de Conexões, no passo de configurar este canal. */
  readonly aoAdicionarCanal?: (canal: TipoDeCanal) => void;
  /** Leva à lista de canais, em Conexões. */
  readonly aoAbrirConexoes?: () => void;
}

/**
 * As leituras de integração SÓ para quem vê integração.
 *
 * Um componente à parte, e não um `if` dentro do outro, porque hook não se
 * chama condicionalmente: para o gestor, as duas consultas nem saem — a API as
 * recusaria, e a recusa viraria um erro numa tela que não as mostra.
 */
export function SecaoFuncionalidades(props: PropsDaLoja): ReactNode {
  return props.veIntegracao ? <ComIntegracao {...props} /> : <Loja {...props} integracao={null} />;
}

function ComIntegracao(props: PropsDaLoja): ReactNode {
  const plataformas = useCatalogoDePlataformas();
  const canais = useCanaisConectados();

  return (
    <Loja
      {...props}
      integracao={{
        // Nulo enquanto não chega, ou se falhou: a categoria some em vez de
        // dizer "Disponível" para um canal que talvez já esteja conectado.
        plataformas: plataformas.data?.itens ?? null,
        canaisConectados: canais.data?.itens ?? null,
        whatsappConfigurado: aplicativoDaMeta() !== null,
      }}
      /*
       * Falha é a leitura que NÃO tem o que mostrar. Uma nova leitura que
       * falha com a anterior ainda na tela não tirou nada da loja, e o aviso
       * de "não foi possível ler" ao lado dos canais listados se contradiria.
       */
      falhaDeIntegracao={
        (plataformas.error !== null && plataformas.data === undefined) ||
        (canais.error !== null && canais.data === undefined)
      }
    />
  );
}

function Loja({
  stepUp,
  editaFuncionalidades,
  conectaCanais,
  aoAdicionarCanal,
  aoAbrirConexoes,
  integracao,
  falhaDeIntegracao = false,
}: PropsDaLoja & {
  integracao: FontesDaLoja['integracao'];
  falhaDeIntegracao?: boolean;
}): ReactNode {
  const consulta = useFlags();
  const [categoria, definirCategoria] = useState<CategoriaDaLoja | null>(null);
  const [busca, definirBusca] = useState('');
  const [aberto, definirAberto] = useState<string | null>(null);
  /**
   * O recado da última mudança ("Adicionada à organização: …").
   *
   * Mora aqui, e não no detalhe, porque o item pode sumir da loja ao ser
   * removido — o portal de um segmento só aparece para quem o tem — e o
   * recado tem de sobreviver à volta para a vitrine.
   */
  const [aviso, definirAviso] = useState<string | null>(null);
  /** O cartão de onde se abriu o detalhe, para o foco voltar a ele. */
  const voltarPara = useRef<string | null>(null);
  const vitrine = useRef<HTMLElement>(null);

  const itens = consulta.data ? montarLoja({ flags: consulta.data.itens, integracao }) : [];
  const item = aberto === null ? undefined : itens.find((i) => i.id === aberto);
  const sumiu = aberto !== null && consulta.data !== undefined && item === undefined;

  // O item aberto saiu da loja: a vitrine volta, e `aberto` não fica apontando
  // para um id que não existe.
  useEffect(() => {
    if (sumiu) definirAberto(null);
  }, [sumiu]);

  useEffect(() => {
    if (aberto !== null || voltarPara.current === null) return;
    vitrine.current?.querySelector<HTMLElement>(`[data-item="${voltarPara.current}"]`)?.focus();
    voltarPara.current = null;
  }, [aberto]);

  const categorias = CATEGORIAS_DA_LOJA.filter((c) => itens.some((i) => i.categoria === c.chave));
  // A categoria escolhida pode sumir (a leitura dos canais falhou depois de
  // escolhida): aí vale "todas", em vez de uma vitrine vazia sem saída.
  const categoriaEfetiva = categorias.some((c) => c.chave === categoria) ? categoria : null;

  return (
    <Estado consulta={consulta} forma="cartoes" linhas={4}>
      {() => {
        if (item) {
          return (
            <DetalheDoItem
              item={item}
              stepUp={stepUp}
              editaFuncionalidades={editaFuncionalidades}
              conectaCanais={conectaCanais}
              aviso={aviso}
              aoAvisar={definirAviso}
              aoVoltar={() => definirAberto(null)}
              aoAdicionarCanal={aoAdicionarCanal}
              aoAbrirConexoes={aoAbrirConexoes}
            />
          );
        }

        const visiveis = filtrarLoja(itens, categoriaEfetiva, busca);
        // A contagem é da categoria do título ao lado: "IA · 12 itens" sobre um
        // cartão só faria a pessoa ler que a IA tem doze.
        const daCategoria =
          categoriaEfetiva === null ? itens : itens.filter((i) => i.categoria === categoriaEfetiva);
        const adicionados = daCategoria.filter((i) => i.estado === 'adicionada').length;

        return (
          <section className="loja" aria-label="Funcionalidades" ref={vitrine}>
            <CapaDaLoja
              busca={busca}
              aoBuscar={definirBusca}
              categoria={categoriaEfetiva}
              categorias={categorias}
              aoEscolherCategoria={definirCategoria}
            />

            {aviso && (
              <p className="admin__nota admin__nota--ok" role="status">
                {aviso}
              </p>
            )}

            {falhaDeIntegracao && (
              <Faixa tom="atencao">
                Não foi possível ler os canais ou os portais agora. Eles voltam para a loja assim
                que a leitura voltar.
              </Faixa>
            )}

            <div className="loja__cabeca-da-grade">
              <h3 className="loja__titulo-da-grade">
                {categoriaEfetiva === null
                  ? 'Todas as categorias'
                  : nomeDaCategoria(categoriaEfetiva)}
              </h3>
              <span className="loja__contagem">
                {daCategoria.length} {daCategoria.length === 1 ? 'item' : 'itens'} · {adicionados}{' '}
                {adicionados === 1 ? 'adicionado' : 'adicionados'}
              </span>
            </div>

            {visiveis.length === 0 ? (
              <p className="admin__nota" role="status">
                {busca.trim() === ''
                  ? 'Nada nesta categoria por enquanto.'
                  : `Nada encontrado para “${busca.trim()}”.`}
              </p>
            ) : (
              <ul className="loja__vitrine">
                {visiveis.map((i) => (
                  <li key={i.id}>
                    <CartaoDoItem
                      item={i}
                      aoAbrir={() => {
                        voltarPara.current = i.id;
                        definirAviso(null);
                        definirAberto(i.id);
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      }}
    </Estado>
  );
}

function nomeDaCategoria(categoria: CategoriaDaLoja): string {
  return CATEGORIAS_DA_LOJA.find((c) => c.chave === categoria)?.nome ?? categoria;
}

// ── A capa ──────────────────────────────────────────────────────────────────

/** Os glifos do mosaico, na ordem em que se repetem. */
const GLIFOS_DO_MOSAICO: readonly NomeDoIcone[] = [
  'whatsapp',
  'instagram',
  'facebook',
  'web-chat',
  'portal',
  'tiktok',
  'ia',
  'api',
  'sino',
];

/** A distância entre dois ladrilhos da mesma fileira. */
const PASSO_DO_LADRILHO = 96;

/**
 * O mosaico de fundo: quatro fileiras de ladrilhos, desencontradas, cada uma
 * começando num glifo diferente para a repetição não formar coluna.
 *
 * Desenhado aqui, e não numa imagem, por causa do tema: o ladrilho e o glifo
 * vêm dos tokens, e o mosaico escurece junto com o resto no tema escuro.
 *
 * As fileiras correm, e a volta não pode dar salto: o CSS as desloca uma
 * sequência inteira de glifos (9 × 96 = 864), e cada uma desenha essa sequência
 * a mais à direita, além de um ladrilho antes da borda esquerda. Assim o que
 * aparece no fim da volta é o mesmo que aparece no começo.
 */
const FILEIRAS_DO_MOSAICO = [0, 1, 2, 3].map((fileira) =>
  Array.from({ length: 15 + GLIFOS_DO_MOSAICO.length + 2 }, (_, indice) => {
    const coluna = indice - 1;
    return {
      x: (fileira % 2 === 0 ? 8 : 56) + coluna * PASSO_DO_LADRILHO,
      y: 18 + fileira * PASSO_DO_LADRILHO,
      glifo:
        GLIFOS_DO_MOSAICO[
          ([0, 3, 6, 1][fileira]! + coluna + GLIFOS_DO_MOSAICO.length) % GLIFOS_DO_MOSAICO.length
        ]!,
    };
  }),
);

/** A ilustração da direita: o CRM no centro, ligado aos apps em volta. */
const APPS_DA_CAPA: readonly { glifo: NomeDoIcone; faixa: string }[] = [
  { glifo: 'whatsapp', faixa: 'whatsapp' },
  { glifo: 'ia', faixa: 'ia' },
  { glifo: 'instagram', faixa: 'instagram' },
  { glifo: 'facebook', faixa: 'facebook' },
  { glifo: 'tiktok', faixa: 'tiktok' },
  { glifo: 'web-chat', faixa: 'web_chat' },
  { glifo: 'portal', faixa: 'portais' },
];

/** O centro dos apps, na mesma ordem, para as linhas que os ligam ao CRM. */
const PONTAS_DAS_LINHAS = [
  [70, 58],
  [210, 30],
  [350, 58],
  [46, 168],
  [374, 168],
  [140, 232],
  [280, 232],
] as const;

function CapaDaLoja({
  busca,
  aoBuscar,
  categoria,
  categorias,
  aoEscolherCategoria,
}: {
  busca: string;
  aoBuscar: (busca: string) => void;
  categoria: CategoriaDaLoja | null;
  categorias: readonly { chave: CategoriaDaLoja; nome: string }[];
  aoEscolherCategoria: (categoria: CategoriaDaLoja | null) => void;
}): ReactNode {
  return (
    <div className="loja-capa">
      <svg
        className="loja-capa__fundo"
        viewBox="0 0 1440 400"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
        focusable="false"
      >
        <circle className="loja-capa__mancha" cx="1290" cy="30" r="230" />
        <circle className="loja-capa__mancha" cx="110" cy="400" r="190" />
        {FILEIRAS_DO_MOSAICO.map((ladrilhos, fileira) => (
          <g
            key={fileira}
            className={`loja-capa__fileira${fileira % 2 === 1 ? ' loja-capa__fileira--volta' : ''}`}
          >
            {ladrilhos.map((l) => (
              <g key={l.x} transform={`translate(${l.x} ${l.y})`}>
                <rect className="loja-capa__ladrilho" width="56" height="56" rx="14" />
                <g className="loja-capa__glifo" transform="translate(16 16) scale(1.2)">
                  <Icone nome={l.glifo} />
                </g>
              </g>
            ))}
          </g>
        ))}
      </svg>

      <div className="loja-capa__texto">
        <h2 className="loja-capa__titulo">Apps e integrações para a sua organização</h2>
        <p className="loja-capa__apoio">
          Canais, portais e recursos que a organização adiciona quando quiser. Procure pelo nome ou
          escolha uma categoria.
        </p>
        <div className="loja-capa__busca">
          <Campo
            rotuloOculto="Buscar funcionalidade"
            type="search"
            tamanho="alto"
            placeholder="Buscar por nome ou pelo que faz"
            value={busca}
            onChange={(e) => aoBuscar(e.target.value)}
          />
          <CampoSelecao
            rotuloOculto="Categoria"
            tamanho="alto"
            value={categoria ?? ''}
            onChange={(e) =>
              aoEscolherCategoria(categorias.find((c) => c.chave === e.target.value)?.chave ?? null)
            }
          >
            <option value="">Todas as categorias</option>
            {categorias.map((c) => (
              <option key={c.chave} value={c.chave}>
                {c.nome}
              </option>
            ))}
          </CampoSelecao>
        </div>
      </div>

      <div className="loja-capa__ilustracao" aria-hidden="true">
        <svg className="loja-capa__linhas" viewBox="0 0 420 264" focusable="false">
          {PONTAS_DAS_LINHAS.map(([x, y]) => (
            <line key={`${x}-${y}`} x1="210" y1="130" x2={x} y2={y} />
          ))}
        </svg>
        <span className="loja-capa__centro">
          <img src="/marca/simbolo-cliqcrm-claro.png" alt="" />
        </span>
        {APPS_DA_CAPA.map((app, indice) => (
          <span
            key={app.glifo}
            className={`loja-capa__app loja-capa__app--${indice + 1}`}
            data-faixa={app.faixa}
          >
            <Icone nome={app.glifo} />
          </span>
        ))}
      </div>
    </div>
  );
}

// ── O cartão da vitrine ─────────────────────────────────────────────────────

/**
 * O que pinta a faixa: o tipo do canal; a marca, para a logo que pede fundo
 * próprio, e o fundo neutro para as outras logos; a categoria para o resto.
 */
function chaveDaFaixa(item: ItemDaLoja): string {
  if ('canal' in item.icone) return item.icone.canal;
  if ('imagem' in item.icone) return item.icone.fundo ?? 'logo';
  return item.categoria;
}

/**
 * A faixa de cima do cartão: o glifo e o nome sobre a cor da marca, ou a logo
 * do sistema de fora.
 *
 * A logo vem sem o nome ao lado: ela já é o nome, escrito como a marca o
 * escreve. Decorativa para o leitor de tela nos dois casos — o nome vem logo
 * abaixo, no corpo do cartão, e lê-lo duas vezes seria ruído.
 */
function FaixaDoItem({ item, grande = false }: { item: ItemDaLoja; grande?: boolean }): ReactNode {
  const { icone } = item;
  return (
    <span
      className={grande ? 'loja-faixa loja-faixa--grande' : 'loja-faixa'}
      data-faixa={chaveDaFaixa(item)}
      aria-hidden="true"
    >
      {'imagem' in icone ? (
        <img className="loja-faixa__logo" src={icone.imagem} alt="" />
      ) : (
        <>
          <Icone nome={'canal' in icone ? iconeDoCanal(icone.canal) : icone.nome} />
          <span className="loja-faixa__nome">{item.nome}</span>
        </>
      )}
    </span>
  );
}

/** O estado em palavra. Em breve e indisponível são informação, não alerta. */
function EstadoDoItem({ item }: { item: ItemDaLoja }): ReactNode {
  const rotulo = ROTULO_DO_ESTADO[item.estado];
  if (item.estado === 'adicionada') return <Selo tom="ok">{rotulo}</Selo>;
  if (item.estado === 'disponivel') return <Selo>{rotulo}</Selo>;
  return <Chip>{rotulo}</Chip>;
}

/**
 * O cartão inteiro é o botão que abre o detalhe — inclusive o "Em breve": o
 * detalhe diz o que ele vai fazer e por que ainda não dá, e é isso que evita a
 * pergunta ao suporte.
 */
function CartaoDoItem({ item, aoAbrir }: { item: ItemDaLoja; aoAbrir: () => void }): ReactNode {
  return (
    <button
      type="button"
      className="loja__cartao"
      data-item={item.id}
      data-estado={item.estado}
      onClick={aoAbrir}
    >
      <FaixaDoItem item={item} />
      <span className="loja__cartao-corpo">
        <span className="loja__cartao-cabeca">
          <span className="loja__nome">{item.nome}</span>
          <EstadoDoItem item={item} />
        </span>
        <span className="loja__linha">{item.linha}</span>
        <span className="loja__categoria">{nomeDaCategoria(item.categoria)}</span>
      </span>
    </button>
  );
}

// ── O detalhe ───────────────────────────────────────────────────────────────

function DetalheDoItem({
  item,
  stepUp,
  editaFuncionalidades,
  conectaCanais,
  aviso,
  aoAvisar,
  aoVoltar,
  aoAdicionarCanal,
  aoAbrirConexoes,
}: {
  item: ItemDaLoja;
  stepUp: UsoDeStepUp;
  editaFuncionalidades: boolean;
  conectaCanais: boolean;
  aviso: string | null;
  aoAvisar: (aviso: string | null) => void;
  aoVoltar: () => void;
  aoAdicionarCanal?: (canal: TipoDeCanal) => void;
  aoAbrirConexoes?: () => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const { recarregarMe } = useSessao();
  const titulo = useRef<HTMLHeadingElement>(null);
  const [removendo, definirRemovendo] = useState(false);

  // O foco vai para o título: quem abriu pelo teclado precisa saber onde caiu.
  useEffect(() => titulo.current?.focus(), []);

  /*
   * Ligar É homologar (princípio 08): a linha só existe com quem ligou e
   * quando.
   *
   * O `comStepUp` fica em volta, mas a API não pede step-up para funcionalidade
   * hoje — `alterarFlag` não chama `exigirStepUp`, e não há ação de
   * funcionalidade em `ACOES_SENSIVEIS`. Ele existe para o dia em que pedir.
   *
   * E é por isso que o recado e a invalidação moram DENTRO da ação, e não no
   * `onSuccess`: quando o step-up é pedido, `comStepUp` abre o diálogo e
   * RESOLVE sem ter feito nada, e refaz a ação depois da concessão, fora da
   * mutação. No `onSuccess`, o recado diria "adicionada" antes de qualquer
   * mudança — e continuaria dizendo se a pessoa cancelasse.
   */
  const alterar = useMutation({
    mutationFn: async ({ chave, ligada }: { chave: string; ligada: boolean }) => {
      await stepUp.comStepUp(async () => {
        await api!.alterarFlag({
          caminho: { chave },
          corpo: {
            ligada,
            justificativa: ligada
              ? 'homologado pela organização na loja de funcionalidades'
              : 'removido pela organização na loja de funcionalidades',
          },
        });
        definirRemovendo(false);
        aoAvisar(
          ligada
            ? `Adicionada à organização: ${item.nome}.`
            : `Removida da organização: ${item.nome}.`,
        );
        // A árvore inteira de administração: o catálogo de portais mora nela, e
        // a funcionalidade muda a configuração efetiva.
        void cache.invalidateQueries({ queryKey: chaves.administracao() });
        // E o `/v1/me`, de onde o menu e as telas tiram as funcionalidades. As
        // outras pessoas recebem o aviso pelo fluxo; esta não depende dele.
        void recarregarMe();
      }, chave);
    },
  });

  const semConexao = 'Sem conexão agora. Tente de novo em instantes.';
  const naoEdita = 'Só quem administra a organização adiciona ou remove.';
  const acao = item.acao;

  const acoes = ((): ReactNode => {
    if (item.estado === 'em-breve' || item.estado === 'indisponivel' || acao.tipo === 'nenhuma') {
      return item.motivo ? <p className="admin__nota">{item.motivo}</p> : null;
    }

    if (acao.tipo === 'canal') {
      const motivoDoBotao = !conectaCanais
        ? 'Só quem administra a organização adiciona canais.'
        : 'Adicionar canal não está disponível nesta tela.';
      const adicionarBotao = (
        <Botao
          variante="primario"
          desabilitado={!conectaCanais || aoAdicionarCanal === undefined}
          motivo={motivoDoBotao}
          onClick={() => aoAdicionarCanal?.(acao.canal)}
        >
          {item.estado === 'adicionada' ? 'Adicionar outro' : 'Adicionar'}
        </Botao>
      );
      return (
        <>
          <div className="loja__acoes">
            {/*
              Outro do mesmo canal só quando o assistente sabe adicioná-lo: um
              TikTok ou uma API conectados direto no motor estão "Adicionados",
              e o assistente não tem o passo deles.
            */}
            {item.motivo === undefined && adicionarBotao}
            {item.estado === 'adicionada' && aoAbrirConexoes && (
              <Botao onClick={aoAbrirConexoes}>Ver em Conexões</Botao>
            )}
          </div>
          <p className="admin__nota">
            {item.estado === 'adicionada'
              ? 'Para remover, use Desconectar no canal, em Conexões.'
              : 'Adicionar abre o assistente de Conexões, já neste canal.'}
          </p>
        </>
      );
    }

    if (item.estado === 'adicionada') {
      // `permanente` não se desliga: tirá-la seria tirar do ar algo de que o
      // produto depende. O botão some, e o motivo aparece no lugar.
      if (acao.permanente) return <p className="admin__nota">Não pode ser desligada.</p>;
      return (
        <div className="loja__acoes">
          <Botao
            variante="perigo"
            desabilitado={!editaFuncionalidades || alterar.isPending || api === null}
            motivo={!editaFuncionalidades ? naoEdita : api === null ? semConexao : 'Removendo…'}
            onClick={() => {
              aoAvisar(null);
              definirRemovendo(true);
            }}
          >
            Remover
          </Botao>
        </div>
      );
    }

    return (
      <>
        <div className="loja__acoes">
          <Botao
            variante="primario"
            desabilitado={!editaFuncionalidades || alterar.isPending || api === null}
            motivo={!editaFuncionalidades ? naoEdita : api === null ? semConexao : 'Adicionando…'}
            onClick={() => {
              aoAvisar(null);
              alterar.mutate({ chave: acao.chave, ligada: true });
            }}
          >
            {alterar.isPending ? 'Adicionando…' : 'Adicionar'}
          </Botao>
        </div>
        <p className="admin__nota">Fica registrado quem adicionou e quando.</p>
      </>
    );
  })();

  return (
    <section className="loja-detalhe" aria-label={item.nome}>
      <p className="loja-detalhe__voltar">
        <Botao variante="fantasma" miudo onClick={aoVoltar}>
          <Icone nome="voltar" />
          Voltar às funcionalidades
        </Botao>
      </p>

      <div className="loja-detalhe__colunas">
        {/*
          O corpo vem ANTES no documento e à direita na tela: quem lê com leitor
          de tela ouve o nome e o que o item faz antes dos botões, e quem vê
          encontra a faixa e a ação à esquerda, como nas lojas de apps.
        */}
        <div className="loja-detalhe__corpo">
          <h2 className="loja-detalhe__titulo" ref={titulo} tabIndex={-1}>
            {item.nome}
          </h2>

          <h3 className="admin__subtitulo">O que faz</h3>
          <p className="loja-detalhe__texto">{item.sobre}</p>

          {item.exemplos !== undefined && item.exemplos.length > 0 && (
            <>
              <h3 className="admin__subtitulo">O que vai dar para fazer</h3>
              <ul className="loja-detalhe__lista">
                {item.exemplos.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </>
          )}

          <h3 className="admin__subtitulo">O que precisa para funcionar</h3>
          {item.requisitos.length === 0 ? (
            <p className="loja-detalhe__texto">Nada além de adicionar.</p>
          ) : (
            <ul className="loja-detalhe__lista">
              {item.requisitos.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="loja-detalhe__lateral">
          <FaixaDoItem item={item} grande />
          <p className="loja-detalhe__linha">{item.linha}</p>
          <div className="loja-detalhe__selos">
            <EstadoDoItem item={item} />
            <Chip>{nomeDaCategoria(item.categoria)}</Chip>
          </div>

          {acoes}

          {aviso && (
            <p className="admin__nota admin__nota--ok" role="status">
              {aviso}
            </p>
          )}
          {alterar.error && (
            <Faixa tom="bloqueado">
              {recadoDeErro(alterar.error, { generica: 'Não foi possível salvar a mudança.' })}
            </Faixa>
          )}

          {/*
            A referência técnica fica aqui embaixo, para o suporte: é o nome
            que a API usa, e o que aparece no log e no registro de auditoria.
            Na vitrine ela não aparece: quem decide adicionar lê o nome e o que
            faz.
          */}
          <p className="loja-detalhe__referencia">
            <span>Referência para o suporte</span>
            <Identificador tamanho="nota">{item.referencia}</Identificador>
          </p>
        </div>
      </div>

      {removendo && acao.tipo === 'flag' && (
        <Dialogo
          titulo={`Remover ${item.nome}?`}
          aoFechar={() => definirRemovendo(false)}
          botoes={
            <>
              <Botao onClick={() => definirRemovendo(false)}>Cancelar</Botao>
              <Botao
                variante="perigo"
                desabilitado={alterar.isPending}
                motivo="Removendo…"
                onClick={() => alterar.mutate({ chave: acao.chave, ligada: false })}
              >
                {alterar.isPending ? 'Removendo…' : 'Remover'}
              </Botao>
            </>
          }
        >
          <p>
            Deixa de funcionar nesta organização em instantes. Para voltar a usar, é só adicionar de
            novo, com uma nova homologação.
          </p>
        </Dialogo>
      )}
    </section>
  );
}
